import { apiClient } from './api';
import { BATTERY_AMBER_PERCENT } from '../utils/battery';
import {
  ApiBatteryPolicy,
  ApiDrone,
  Drone,
  DroneListResponse,
  DroneOwnership,
  DronePositionUpdate,
  PositionUpdate,
} from '../types/drone';

import { DroneRegistration, DroneRegistrationList } from '../types/registration';

type BatteryPolicyResponse = ApiBatteryPolicy;

/**
 * The server's drone status vocabulary is not the UI's (#508).
 *
 * `models.DroneStatus` is provisioning | idle | flying | error | offline |
 * link_lost | shut_down_in_flight; the UI
 * union is idle | in_flight | returning | landed | error | offline. Only two
 * need translating, and both are unambiguous — but an untranslated `flying`
 * silently fell outside every status check in the UI, which is why the
 * translation lives in one named table rather than inline.
 */
const STATUS_FROM_API: Record<string, Drone['status']> = {
  provisioning: 'offline',
  idle: 'idle',
  flying: 'in_flight',
  error: 'error',
  offline: 'offline',
  link_lost: 'link_lost',
  shut_down_in_flight: 'shut_down_in_flight',
};

/** A status as the API names it, in the UI's vocabulary (#508). */
export const statusFrom = (status: string | undefined): Drone['status'] =>
  STATUS_FROM_API[status ?? ''] ?? 'offline';

/**
 * A battery as the API reports it, in the shape the UI renders.
 *
 * One function for both the fleet list and the live feed (#710), so a drone
 * cannot read healthy on reload and unhealthy a second later, or the reverse.
 */
const batteryFrom = (percent: number | undefined): Drone['battery'] => ({
  percentage: percent ?? null,
  voltage: null,
  // An unknown battery is not an unhealthy one (#577). `?? 0` read a missing
  // reading as flat, so a drone that had never reported was labelled
  // "(Unhealthy)" on the strength of a measurement nobody took. The unknown
  // itself travels in `percentage: null`, which every display path already
  // renders as N/A; this flag only ever claims "known to be unhealthy".
  //
  // The number comes from utils/battery rather than being written out here
  // (#629). It was a literal `20` in this file and another in utils/battery,
  // and a third in the Go constant that decided whether the aircraft lands —
  // three statements of one rule that happened to agree.
  isHealthy: percent === undefined || percent >= BATTERY_AMBER_PERCENT,
});

/**
 * Convert a `drone_position_update` frame into the change it makes (#710).
 *
 * The same translation {@link toDrone} applies to the same keys, so the map
 * shows the same thing whether a drone's last position arrived live or on
 * reload. Heading and speed come with the position (#711), and a frame without
 * a heading means the aircraft does not know it, so it is carried as absent
 * rather than leaving the last one standing. The status rides on the frame
 * since a frame sets it (#787), so a drone that was lost and reports again
 * stops reading "link lost" without a reload.
 */
export const toPositionUpdate = (
  data: DronePositionUpdate['data']
): PositionUpdate => ({
  droneId: data.drone_id,
  position: {
    latitude: data.last_known_position.latitude,
    longitude: data.last_known_position.longitude,
    altitude: data.last_known_position.altitude,
  },
  heading: data.last_known_position.heading,
  speed: data.last_known_position.ground_speed,
  battery: batteryFrom(data.battery_percent),
  // last_heartbeat, as toDrone uses: the server's clock, which is what
  // staleness is measured against (#577), not the aircraft's.
  lastUpdateTime: data.last_heartbeat,
  status: data.status === undefined ? undefined : statusFrom(data.status),
});

/**
 * Convert a drone as the API sends it into the shape the UI renders (#508).
 *
 * The two shapes have never matched. Until this function existed the parsed
 * response was handed to the store untouched, so `drone.position` was
 * `undefined` for every drone that had not pushed telemetry since page load —
 * and the fleet map dereferenced it and crashed.
 *
 * What the API cannot tell us, this does not invent:
 *
 * - `position` stays `undefined` rather than becoming 0,0. A drone at the origin
 *   is a drone in the Atlantic, and on a map that is a lie a pilot could act on.
 * - `heading` and `speed` stay `undefined` when the drone did not report them
 *   (#711). They were a constant 0, which the details panel printed as
 *   "0° N" and "0.0 m/s": a placeholder read as a measurement.
 * - `battery.voltage` is null, not 0. Null renders as "N/A"; zero renders as a
 *   flat battery.
 */
export const toDrone = (api: ApiDrone, ownership: DroneOwnership): Drone => ({
  id: api.id,
  ownership,
  // The drone's own name, or absent (#406). Not the device model, which this
  // used to borrow: two drones of one model then had the same "name", and
  // neither was what anyone had called it. droneLabel falls back to the ID.
  name: api.name || undefined,
  hardwareId: api.hardware_id,
  model: api.device_model || undefined,
  softwareVersion: api.software_version || undefined,
  virtual: api.virtual ?? false,
  credentials: api.credentials
    ? { state: api.credentials.state, expiresAt: api.credentials.expires_at }
    : undefined,
  status: statusFrom(api.status),
  position: api.last_known_position
    ? {
        latitude: api.last_known_position.latitude,
        longitude: api.last_known_position.longitude,
        altitude: api.last_known_position.altitude ?? 0,
      }
    : undefined,
  // Straight from the server, defaulting to none (#578). A drone we have no
  // ruling for offers no flight controls, which is the safe direction: the
  // alternative is a button that arms an aircraft on a guess.
  availableCommands: api.available_commands ?? [],
  heading: api.last_known_position?.heading,
  speed: api.last_known_position?.ground_speed,
  battery: batteryFrom(api.battery_percent),
  // Flattened rather than passed through, so a component reads one shape instead
  // of reaching through `threshold.volts_per_cell` and remembering which keys the
  // server omits (#629). Absent stays absent: a backend that does not send a
  // policy must not be made to look like one that reports the default.
  landingPolicy: api.battery_policy && {
    pack: api.battery_policy.pack,
    thresholdVoltsPerCell: api.battery_policy.threshold.volts_per_cell,
    thresholdDwellSeconds: api.battery_policy.threshold.dwell_seconds,
    isDefault: api.battery_policy.is_default,
    thresholdPercent: api.battery_policy.threshold_percent,
    floorVoltsPerCell: api.battery_policy.floor_volts_per_cell,
    ceilingVoltsPerCell: api.battery_policy.ceiling_volts_per_cell,
  },
  imageUrl: null,
  lastUpdateTime: api.last_heartbeat || api.created_at || '',
  currentMissionId: null,
});

export const droneService = {
  /**
   * Fetch every drone the signed-in operator may see — their own fleet, the
   * unclaimed ones, and for an admin everybody else's — as one list, each
   * tagged with which it is.
   *
   * The endpoint answers with three buckets (#382, #575). Returning only
   * `drones` would drop every factory-registered aircraft off the map, so all
   * three are returned; the `ownership` tag is what lets the fleet view tell
   * them apart (#385) without every component having to know the response
   * shape — or having to work out, from a drone and a `/me` response, whose it
   * is. Only the server knows that, and only the server should answer it.
   *
   * Both buckets go through {@link toDrone}. Spreading the raw response and
   * adding a flag — which is what this did until #508 — produced objects that
   * satisfied the `Drone` type only because TypeScript never saw the wire data.
   */
  async getAllDrones(): Promise<Drone[]> {
    try {
      const body = await apiClient.get<DroneListResponse>('/api/v1/drones');
      return [
        ...(body.drones ?? []).map((drone) => toDrone(drone, 'mine')),
        ...(body.others ?? []).map((drone) => toDrone(drone, 'others')),
        ...(body.unclaimed ?? []).map((drone) => toDrone(drone, 'unclaimed')),
      ];
    } catch (error) {
      console.error('Failed to fetch drones:', error);
      throw error;
    }
  },

  /**
   * Rename a drone (#406). The server trims the name and refuses one that is
   * not 1–64 printable characters; that refusal is what the caller shows.
   */
  async renameDrone(droneId: string, name: string): Promise<void> {
    await apiClient.put<ApiDrone>(`/api/v1/drones/${encodeURIComponent(droneId)}`, { name });
  },

  /**
   * The registration record of one physical drone, without its key, or
   * `null` when it has none (#411).
   *
   * Read from the list, which never decrypts a key, so opening a card is not
   * a read of a credential. Only {@link revealRegistrationKey} is.
   */
  async getRegistration(droneId: string): Promise<DroneRegistration | null> {
    const body = await apiClient.get<DroneRegistrationList>('/api/v1/drones/registrations');
    return body.registrations.find((r) => r.drone_id === droneId) ?? null;
  },

  /**
   * The registration key, decrypted. Every call is an audited read on the
   * server (#649), so this runs only when the pilot asks to see it.
   */
  async revealRegistrationKey(droneId: string): Promise<string> {
    const registration = await apiClient.get<DroneRegistration>(
      `/api/v1/drones/${encodeURIComponent(droneId)}/registration`
    );
    if (!registration.registration_key) {
      throw new Error('The server sent the registration without its key.');
    }
    return registration.registration_key;
  },

  /**
   * Fetch drone image URL
   */
  getDroneImageUrl(droneId: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
    return `${baseUrl}/api/v1/drones/${droneId}/image`;
  },

  /**
   * Set this drone's automatic-landing battery threshold (#629).
   *
   * `threshold: null` is how a pilot returns the drone to the fleet default, and
   * it is a different request from omitting the key, which leaves it alone. The
   * server distinguishes the two deliberately; sending `undefined` would collapse
   * them and make the setting impossible to undo.
   *
   * No validation here. The floor and the ceiling arrive from the server on the
   * drone's own `landingPolicy`, and the refusal arrives from the server too —
   * the browser explains, it does not decide (#578). A copy of the bounds in this
   * file would be another place one rule is written down, and the floor is
   * expected to move once there is recorded current-draw data.
   */
  async setLandingPolicy(
    droneId: string,
    body: {
      pack?: { cells: number; chemistry: string; capacity_mah: number } | null;
      threshold?: { volts_per_cell: number; dwell_seconds: number } | null;
    }
  ): Promise<BatteryPolicyResponse> {
    return apiClient.put<BatteryPolicyResponse>(
      `/api/v1/drones/${encodeURIComponent(droneId)}/battery`,
      body
    );
  },
};
